import { useCreateOrder } from '@/api/api-mutations'
import { useGetOrder } from '@/api/react-query'
import { useGetSubject } from '@/utils/hooks'
import { StoredStringSubject } from '@/utils/storage'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo } from 'react'

export interface OrderContextValue {
	resetOrder: () => Promise<void>
}

const OrderContext = createContext<OrderContextValue>(undefined as any)

export const useOrderContext = () => {
	const value = useContext(OrderContext)
	if (!value) {
		throw new Error('useOrderContext must be used within a OrderProvider')
	}
	return value
}

export const orderIdSubject = new StoredStringSubject('orderId', '')
export const OrderWrapper = ({ children }: { children: ReactNode }) => {
	const createNewOrder = useCreateOrder()
	const resetOrder = useCallback(async () => {
		const result = await createNewOrder.mutateAsync({ body: {} })
		if (result.isSuccess) {
			orderIdSubject.next(result.response.uuid)
		} else {
			throw new Error('Failed to create new order')
		}
	}, [])

	useEffect(() => {
		const orderId = orderIdSubject.value
		if (!orderId) {
			resetOrder()
		}
	}, [])

	const value = useMemo<OrderContextValue>(() => ({ resetOrder }), [resetOrder])
	return <OrderContext.Provider value={value}>{children}</OrderContext.Provider>
}

export const useGetCurrentOrder = (options?: { enabled?: boolean }) => {
	const orderId = useGetSubject(orderIdSubject)
	return useGetOrder({ orderUuid: orderId }, options)
}
